import React, { useState } from 'react';
import {
  Box, Button, Chip, Collapse, Paper, Stack, Table, TableBody, TableCell,
  TableRow, Typography,
} from '@mui/material';
import { BsChevronDown, BsChevronUp } from 'react-icons/bs';

/**
 * Les noms de champs tels qu'ils arrivent de l'agent, et ce qu'ils désignent.
 *
 * Les schémas nomment en anglais et en pointillé (`contract.reference`) :
 * lisible pour le modèle, illisible pour l'expert qui doit valider.
 */
const LIBELLES = {
  title: 'Titre', title_bis: 'Objet', comment: 'Commentaire',
  status: 'Statut', receiver: 'Destinataire', sender: 'Expéditeur',
  copy_receiver: 'Copie à', public: 'Partagée', objet: 'Objet',
  expediteur: 'Expéditeur', destinataire: 'Destinataire', copie: 'Copie à',
  corps: 'Corps du message', letter_content: 'Contenu du courrier',
  name: 'Nom', firstname: 'Prénom', mail: 'Email', email: 'Email',
  phone: 'Téléphone', role1: 'Rôle principal', role2: 'Rôle secondaire',
  reference: 'Référence', street: 'Rue', number: 'Numéro', city: 'Ville',
  postcode: 'Code postal', country: 'Pays',
  item: 'Objet', description: 'Description', amount_ask: 'Montant réclamé',
  amount_buy: "Valeur d'achat", amount_propose: 'Montant retenu',
  date_buy: "Date d'achat",
  reclamer: 'Réclamé', calculer: 'Calculé', accorder: 'Accordé',
  recours: 'Recours', tva: 'TVA', origin: 'Origine', details: 'Lignes',
  unitValue: 'Prix unitaire', quantity: 'Quantité', unit: 'Unité',
  type_field: 'Type de champ', at_date: 'Date', check_box: 'Coché',
  'contract.title': 'Titre du contrat',
  'contract.reference': 'Référence du contrat',
  'contract.start_date': 'Date de souscription',
  'contract.reference_cg': 'Conditions générales',
  'contract.assureur': 'Assureur',
  'claims.reference': 'Référence sinistre',
  'claims.date': 'Date du sinistre',
  'claims.address': 'Adresse du sinistre',
  'claims.declaration': "Déclaration de l'assuré",
  list_id: 'Liste de tâches', template_id: 'Modèle',
  entreprise: 'Entreprise', posts: 'Postes', tasks: 'Tâches',
  emails: 'Courriels', persons: 'Intervenants', entries: 'Champs',
};

/** Les champs qu'écraser fait perdre quelque chose d'irremplaçable. */
const SENSIBLES = new Set(['claims.declaration']);

const libelle = (cle) => LIBELLES[cle] || cle;

const sansBalises = (v) => String(v).replace(/<[^>]*>/g, ' ')
  .replace(/\s+/g, ' ').trim();

const lisible = (valeur) => {
  if (typeof valeur === 'boolean') return valeur ? 'oui' : 'non';
  const texte = String(valeur);
  // Le corps d'un courrier ou une analyse arrivent en HTML.
  return texte.includes('<') ? sansBalises(texte) : texte;
};

/**
 * Une valeur simple, et ce qu'elle remplace.
 *
 * Montrer la nouvelle valeur seule dit ce qu'on gagne, jamais ce qu'on perd.
 * Quand une valeur existe aujourd'hui et diffère, elle s'affiche barrée
 * au-dessus.
 */
function Valeur({ valeur, ancienne }) {
  if (valeur === null || valeur === undefined || valeur === '') {
    return <Box component="span" sx={{ color: 'text.disabled' }}>—</Box>;
  }
  const nouvelle = lisible(valeur);
  const avant = (ancienne === null || ancienne === undefined || ancienne === '')
    ? null : lisible(ancienne);
  const remplace = avant !== null && avant !== nouvelle;

  return (
    <Box>
      {remplace && (
        <Typography variant="caption" color="warning.main" component="div"
                    sx={{ textDecoration: 'line-through', opacity: 0.8 }}>
          {avant}
        </Typography>
      )}
      <Box component="span" sx={{ whiteSpace: 'pre-wrap' }}>{nouvelle}</Box>
    </Box>
  );
}

/** Un objet : ses champs, l'un sous l'autre. */
function Objet({ donnees, actuel, profondeur = 0 }) {
  const entrees = Object.entries(donnees).filter(
    ([, v]) => v !== null && v !== undefined && v !== '');
  if (entrees.length === 0) {
    return <Typography variant="caption" color="text.secondary">
      Aucune valeur.
    </Typography>;
  }
  return (
    <Table size="small">
      <TableBody>
        {entrees.map(([cle, valeur]) => (
          <TableRow key={cle}>
            <TableCell sx={{ border: 0, py: 0.5, pl: 0, width: '35%',
                             verticalAlign: 'top' }}>
              <Stack direction="row" spacing={0.5} alignItems="center"
                     flexWrap="wrap" useFlexGap>
                <Typography variant="caption" color="text.secondary">
                  {libelle(cle)}
                </Typography>
                {(SENSIBLES.has(cle)
                  || (actuel?.[cle] !== undefined
                      && actuel[cle] !== null
                      && String(actuel[cle]) !== String(valeur))) && (
                  <Chip size="small" color="warning" label="remplace" />
                )}
              </Stack>
            </TableCell>
            <TableCell sx={{ border: 0, py: 0.5 }}>
              <Contenu donnees={valeur} actuel={actuel?.[cle]}
                       profondeur={profondeur + 1} />
            </TableCell>
          </TableRow>
        ))}
      </TableBody>
    </Table>
  );
}

/** N'importe quelle valeur : liste, objet, ou simple. */
function Contenu({ donnees, actuel, profondeur = 0 }) {
  if (Array.isArray(donnees)) {
    return (
      <Stack spacing={1}>
        {donnees.map((element, i) => (
          <Box key={i} sx={{ pl: profondeur ? 1.5 : 0,
                             borderLeft: profondeur ? '2px solid' : 0,
                             borderColor: 'divider' }}>
            {/* L'état actuel arrive dans le même ordre que la proposition :
                la position i décrit le même enregistrement. */}
            {typeof element === 'object' && element !== null
              ? <Objet donnees={element} actuel={actuel?.[i]}
                       profondeur={profondeur} />
              : <Valeur valeur={element} ancienne={actuel?.[i]} />}
          </Box>
        ))}
      </Stack>
    );
  }
  if (typeof donnees === 'object' && donnees !== null) {
    return <Objet donnees={donnees} actuel={actuel} profondeur={profondeur} />;
  }
  return <Valeur valeur={donnees} ancienne={actuel} />;
}

/** « 3 tâches », « 2 postes » — ce que la proposition pèse, en un mot. */
function resume(donnees) {
  if (Array.isArray(donnees)) {
    return `${donnees.length} élément${donnees.length > 1 ? 's' : ''}`;
  }
  if (typeof donnees !== 'object' || donnees === null) return '';
  const listes = Object.entries(donnees).filter(([, v]) => Array.isArray(v));
  if (listes.length) {
    return listes.map(([cle, v]) => `${v.length} ${libelle(cle).toLowerCase()}`)
      .join(', ');
  }
  const remplis = Object.values(donnees).filter(
    (v) => v !== null && v !== undefined && v !== '').length;
  return `${remplis} champ${remplis > 1 ? 's' : ''}`;
}

/**
 * Ce que l'agent propose d'écrire, avant de le confirmer.
 *
 * La carte n'affichait qu'un libellé : « Créer les postes de coût », sans
 * dire lesquels ni pour combien. Valider revenait à signer sans lire.
 */
export default function Proposition({ proposition, faite, enCours,
                                      onConfirmer }) {
  const donnees = proposition?.data;
  const actuel = proposition?.actuel;
  const sensible = JSON.stringify(donnees || {}).includes('claims.declaration');
  // `actuel` n'est renseigné que là où quelque chose existe déjà.
  const remplace = Boolean(actuel)
    && JSON.stringify(actuel) !== 'null' && JSON.stringify(actuel) !== '[]';

  const gros = Array.isArray(donnees) ? donnees.length > 2
    : Object.keys(donnees || {}).length > 6;
  // Une proposition qui remplace s'ouvre même si elle est longue : c'est
  // justement celle qu'il ne faut pas valider sans lire.
  const [ouvert, setOuvert] = useState(!gros || remplace);

  return (
    <Paper variant="outlined"
           sx={{ mt: 1, borderColor: faite ? 'success.main' : 'warning.main' }}>
      <Stack direction="row" spacing={2} alignItems="center"
             justifyContent="space-between" sx={{ p: 1.5 }}>
        <Box sx={{ minWidth: 0 }}>
          <Typography variant="body2" sx={{ fontWeight: 600 }}>
            {proposition.label || 'Action proposée'}
          </Typography>
          <Stack direction="row" spacing={1} alignItems="center"
                 flexWrap="wrap" useFlexGap>
            <Typography variant="caption" color="text.secondary">
              {resume(donnees)}
            </Typography>
            {remplace && !faite && (
              <Chip size="small" color="warning"
                    label="remplace des valeurs existantes" />
            )}
          </Stack>
        </Box>
        <Stack direction="row" spacing={1} alignItems="center">
          <Button size="small" onClick={() => setOuvert(!ouvert)}
                  endIcon={ouvert ? <BsChevronUp /> : <BsChevronDown />}>
            {ouvert ? 'Masquer' : 'Voir'}
          </Button>
          {faite ? (
            <Chip size="small" color="success" label="Fait" />
          ) : (
            <Button size="small" variant="contained" color="success"
                    onClick={onConfirmer} disabled={enCours}>
              {enCours ? 'En cours…' : 'Confirmer'}
            </Button>
          )}
        </Stack>
      </Stack>

      <Collapse in={ouvert}>
        <Box sx={{ px: 1.5, pb: 1.5, borderTop: '1px solid',
                   borderColor: 'divider', pt: 1.5 }}>
          {sensible && (
            <Typography variant="caption" color="warning.main"
                        component="div" sx={{ mb: 1 }}>
              Cette action remplace la déclaration écrite par l'assuré. Ses
              mots sont ceux qui font foi face à la compagnie.
            </Typography>
          )}
          {remplace && (
            <Typography variant="caption" color="text.secondary"
                        component="div" sx={{ mb: 1 }}>
              Ce qui est barré en orange sera remplacé.
            </Typography>
          )}
          <Contenu donnees={donnees} actuel={actuel} />
        </Box>
      </Collapse>
    </Paper>
  );
}
